import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Stack from '@mui/material/Stack/Stack';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';
import Typography from '@mui/material/Typography/Typography';
import Chip from '@mui/material/Chip/Chip';
import Alert from '@mui/material/Alert/Alert';
import Box from '@mui/material/Box/Box';
import Button from '@mui/material/Button/Button';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Autocomplete from '@mui/material/Autocomplete/Autocomplete';
import TextField from '@mui/material/TextField/TextField';
import IconButton from '@mui/material/IconButton/IconButton';
import LinearProgress from '@mui/material/LinearProgress/LinearProgress';
import { nextToScore, stepTeam } from '../../utils/teamPicker';
import { HackathonPayload } from '../../services/hackathon.service';
import { SessionPayload } from '../../services/session.service';
import { useLiveBoard } from '../../hooks/useLiveSessions';
import { useSessions } from '../../hooks/useSessions';
import { categoryLabels, dueTeams, voteEligibility } from '../../utils/sessions';
import { useMyVotes } from '../../hooks/useMyVotes';
import { voteKey } from '../../services/vote.service';
import { JudgeVoteCard } from '../Voting/JudgeVoteCard';
import { participantsScore } from '../../utils/sessions';

type TeamState = 'on stage' | 'your team' | 'scored' | 'to score' | 'not yet';

/** A team's state in the picker: on stage glows, scored is ticked, the rest are words. */
const TeamStatus = ({ status }: { status: TeamState }) => {
    if (status === 'scored') {
        return <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'success.main', flexShrink: 0 }}><CheckCircleIcon sx={{ fontSize: 16 }} /><Typography variant="caption">scored</Typography></Stack>;
    }
    if (status === 'on stage') {
        return <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', boxShadow: theme => `0 0 8px 1px ${theme.palette.primary.main}` }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>on stage</Typography>
        </Stack>;
    }
    return <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{status === 'not yet' ? "hasn't presented" : status}</Typography>;
};

// the team each room's card is on, kept outside the component: the panel can be remounted by a reload while
// someone is mid-score, and starting again from the team on stage put Hermione's score on the next team (run029)
const pickedByRoom = new Map<string, string>();

/**
 * Voting from a session page: pick any of the session's teams (the one on stage is selected first). A room can have
 * 40+ teams, so they're picked from a searchable list with previous / next and "next to score", not laid out at once.
 * Judges score as judges here; everyone else votes when participant voting is on.
 * The card opens on the team on stage and then holds still; when the stage moves on it offers the new team instead.
 */
export const SessionVotePanel = ({
    hackathonId,
    hackathon,
    session,
    viewerEmail
}: {
    hackathonId: string,
    hackathon: HackathonPayload,
    session: SessionPayload,
    viewerEmail?: string
}) => {
    const board = useLiveBoard(hackathonId);
    const { sessions } = useSessions(hackathonId);
    const live = board.sessions.find(s => s._id === session._id);
    // the live lineup (polled every few seconds) over the session list, so a team the hosts just left out disappears
    const lineup = live?.ideas ?? session.ideas;
    const asJudge = !!viewerEmail && session.judges.some(j => j.email === viewerEmail);
    const [picked, setPickedState] = useState<string | null>(() => pickedByRoom.get(session._id) || null);
    const setPicked = (id: string) => {
        pickedByRoom.set(session._id, id);
        setPickedState(id);
    };
    const scored = useMyVotes(hackathonId);
    const [unsaved, setUnsaved] = useState(false);
    const location = useLocation();
    const canTakePart = !!viewerEmail && lineup.length > 0
        && (asJudge || (participantsScore(hackathon, session) && !hackathon.judges.some(j => j.email === viewerEmail)));

    // the live banner's Vote button links to #voting; the router doesn't scroll to hashes itself
    // the card starts on the team on stage and then stays put: switching is always your tap (or "Score <team>"),
    // so a score you're entering can't end up under the next team
    const startTeam = live ? (live.activeIdea && lineup.includes(live.activeIdea) && !hackathon.ideas.find(i => i._id === live.activeIdea)?.members.some(m => m.email === viewerEmail)
        ? live.activeIdea
        : lineup.find(id => !hackathon.ideas.find(i => i._id === id)?.members.some(m => m.email === viewerEmail)) || null) : null;
    useEffect(() => {
        if (!picked && startTeam) {
            setPicked(startTeam);
        }
    }, [picked, startTeam]);

    useEffect(() => {
        if (location.hash === '#voting' && canTakePart) {
            window.setTimeout(() => document.getElementById('voting')?.scrollIntoView({ behavior: 'smooth' }), 100);
        }
    }, [location.hash, canTakePart]);

    if (!canTakePart) {
        return null;
    }

    const isScored = (id: string) => scored.has(voteKey(id, session._id));
    const ownTeam = (id: string) => !!hackathon.ideas.find(i => i._id === id)?.members.some(m => m.email === viewerEmail);
    const onStage = live?.activeIdea && lineup.includes(live.activeIdea) ? live.activeIdea : null;
    const ideaId = picked && lineup.includes(picked) ? picked : (onStage && !ownTeam(onStage) ? onStage : lineup.find(id => !ownTeam(id)) || lineup[0]);
    const idea = hackathon.ideas.find(i => i._id === ideaId);
    if (!idea) {
        return null;
    }
    // where you are: teams that have presented (or the whole lineup before anyone has), never your own
    const due = (live ? dueTeams(live) : lineup).filter(id => !ownTeam(id));
    const scoredDue = due.filter(isScored).length;
    // every team in the lineup but your own is scored
    const allDone = due.length > 0 && scoredDue >= due.length && due.length === lineup.filter(id => !ownTeam(id)).length;
    const ownOnStage = !!onStage && ownTeam(onStage);
    // the stage moved on while you were on another team: offer it rather than swap the card under you
    const stageTeam = onStage && onStage !== ideaId && !ownTeam(onStage) && !isScored(onStage) ? hackathon.ideas.find(i => i._id === onStage) : undefined;
    const eligibility = voteEligibility(hackathon, session, live, ideaId, viewerEmail, {
        single: sessions.length <= 1,
        votingPeriodOn: board.votingPeriodOn
    });
    // the team picker: what each team is to you, and where previous / next / next-to-score go
    const teamOf = (id: string) => hackathon.ideas.find(i => i._id === id);
    const presentedSoFar = new Set(live ? dueTeams(live) : lineup);
    const statusOf = (id: string): TeamState => id === live?.activeIdea ? 'on stage' : ownTeam(id) ? 'your team'
        : isScored(id) ? 'scored' : presentedSoFar.has(id) ? 'to score' : 'not yet';
    const prevTeam = stepTeam(lineup, ideaId, -1, ownTeam);
    const nextTeam = stepTeam(lineup, ideaId, 1, ownTeam);
    const toScore = nextToScore(lineup, ideaId, id => presentedSoFar.has(id), isScored, ownTeam);
    const categories = categoryLabels(hackathon, session.categories);
    const judgeOnly = new Set(hackathon.categories.filter((_, i) => hackathon.categoryMeta[i]?.judgeOnly));
    const judgesOnlyHere = categories.filter(c => judgeOnly.has(c));

    return (
        <Card id="voting" sx={{ scrollMarginTop: 136 }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <HowToVoteIcon color="primary" />
                        <Typography variant="h4" component="h2">Vote</Typography>
                        {asJudge && <Chip size="small" color="primary" label="as a judge" />}
                    </Stack>
                    {
                        categories.length > 0 && (
                            <Typography variant="body2" color="text.secondary">
                                {session.name} judges {categories.join(', ')}.
                                {!asJudge && judgesOnlyHere.length > 0 && ` ${judgesOnlyHere.join(', ')} ${judgesOnlyHere.length === 1 ? 'is' : 'are'} scored by judges only.`}
                            </Typography>
                        )
                    }
                    {
                        due.length > 0 && !allDone && <Stack spacing={0.75}>
                            <Typography variant="body2" role="status">
                                {scoredDue >= due.length
                                    ? <>You've scored {due.length === 1 ? 'the' : 'all'} <b>{due.length}</b> {due.length === 1 ? 'team' : 'teams'}{due.length < lineup.filter(id => !ownTeam(id)).length ? ' presented so far' : ''}.</>
                                    : <>You've scored <b>{scoredDue} of {due.length}</b> {due.length === 1 ? 'team' : 'teams'}{due.length < lineup.filter(id => !ownTeam(id)).length ? ' presented so far' : ''}.</>}
                            </Typography>
                            <LinearProgress variant="determinate" value={(scoredDue / due.length) * 100} aria-hidden
                                sx={{ height: 6, borderRadius: 999, backgroundColor: 'var(--surface-muted)' }} />
                        </Stack>
                    }
                    {/* which team: a searchable list in presenting order, previous / next, and the next one you still have to score */}
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <IconButton aria-label="Previous team" disabled={!prevTeam} onClick={() => prevTeam && setPicked(prevTeam)}><ChevronLeftIcon /></IconButton>
                        <Autocomplete
                            size="small"
                            sx={{ flex: 1, minWidth: 220 }}
                            options={lineup.filter(id => !!teamOf(id))}
                            value={ideaId}
                            disableClearable
                            autoHighlight
                            getOptionLabel={id => `${lineup.indexOf(id) + 1}. ${teamOf(id)?.title || ''}`}
                            // you never score your own team
                            getOptionDisabled={ownTeam}
                            onChange={(_, id) => id && setPicked(id)}
                            renderOption={({ key, ...props }, id) => (
                                <li key={key} {...props}>
                                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: theme => theme.brand.mono, minWidth: '2.5ch', textAlign: 'right' }}>{lineup.indexOf(id) + 1}</Typography>
                                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{teamOf(id)?.title}</Typography>
                                        <TeamStatus status={statusOf(id)} />
                                    </Stack>
                                </li>
                            )}
                            renderInput={params => <TextField {...params} label={`Team to vote on (${lineup.length})`} />}
                        />
                        <IconButton aria-label="Next team" disabled={!nextTeam} onClick={() => nextTeam && setPicked(nextTeam)}><ChevronRightIcon /></IconButton>
                        {toScore && <Button size="small" variant="outlined" onClick={() => setPicked(toScore)} sx={{ whiteSpace: 'nowrap' }}>Next to score</Button>}
                    </Stack>
                    {
                        stageTeam && <Alert severity="info" role="status"
                            action={<Button color="inherit" size="small" onClick={() => setPicked(stageTeam._id)}>Score {stageTeam.title}</Button>}>
                            <b>{stageTeam.title}</b> is on stage now.
                        </Alert>
                    }
                    {
                        // your own team is up: the card is on the next one you can score (Rhea: "why is the vote on the next team?")
                        ownOnStage && <Typography variant="body2" color="text.secondary">Your team is presenting now; you can score the others.</Typography>
                    }
                    {/* which team these scores are for, in words, right above the buttons */}
                    <Typography variant="h6" component="h3" sx={{ overflowWrap: 'anywhere' }}>
                        Scoring: {idea.title}{
                            unsaved
                                ? <Typography component="span" color="warning.main" sx={{ ml: 1, fontWeight: 600 }}>· unsaved changes</Typography>
                                : isScored(ideaId) && <Typography component="span" color="success.main" sx={{ ml: 1, fontWeight: 600 }}>· saved</Typography>
                        }
                    </Typography>
                    {
                        eligibility.allowed
                            ? <Box onPointerDownCapture={() => setPicked(ideaId)} onKeyDownCapture={() => setPicked(ideaId)}>
                                <JudgeVoteCard key={`${session._id}:${ideaId}`} hackathon={hackathon} idea={idea} session={session} onDirtyChange={setUnsaved} />
                            </Box>
                            : <Alert severity="info">{eligibility.reason}</Alert>
                    }
                    {
                        // right under the card you just submitted, where the eye is (Desi: "invisible" above the chips)
                        allDone && <Alert severity="success" role="status" action={<Button color="inherit" size="small" component={Link} to={asJudge ? `/h/${hackathon.slug}/judge` : `/h/${hackathon.slug}`}>{asJudge ? 'See the scores' : 'Back to the event'}</Button>}>
                            <b>All done:</b> you've scored all {due.length} {due.length === 1 ? 'team' : 'teams'}. You can change a score until voting closes.
                        </Alert>
                    }
                </Stack>
            </CardContent>
        </Card>
    );
}
